import { useCallback, useContext, useMemo } from 'react';
import { ConversationItem } from '@zg-rentals/satellite-components';
import ConditionalWrapper from '../../ConditionalWrapper';
import ConversationItemFooter from './ConversationItemFooter';
import SwipeableWrapper from './SwipeableWrapper';
import { CONVERSATION_ITEM_SELECT_EVENT_TYPE_ID } from '../../../constants/clickstreamEvents/conversationItem';
import { AppContext } from '../../../contexts/AppContextProvider';
import { InboxPageContext } from '../../../contexts/InboxPageContextProvider';
import conversationUtils from '../../../utils/conversationUtils';
import type { ConversationListConversation, ConversationSelectArgs } from '../index';
import useDevices from '../../../hooks/useDevices';

type ConversationListItemProps = {
  conversation: ConversationListConversation;
  onSelect: (args: ConversationSelectArgs) => void;
};

export default function ConversationItemContainer({ conversation, onSelect }: ConversationListItemProps) {
  const { isImpersonator } = useContext(AppContext);
  const { conversationId: conversationIdFromUrl } = useContext(InboxPageContext);
  const { isDesktopView, isMobileView } = useDevices();

  const {
    activeParticipants,
    conversationId,
    hasUnreadMessage,
    isConversationBlockedByZillow,
    isPhoneLead,
    listingDetails,
    mostRecentMessage,
    renterName,
    renterPhone,
    status,
    statusLabel,
  } = conversation;

  const { isArchived, isFavorite, isSpam } = status;

  const { isMessageOwner, message: messagePreview, messageDateMs } = mostRecentMessage || {};

  const streetAddress = listingDetails?.displayAddress?.split(',')[0] || '';
  const isStarConversationIconDisabled =
    isImpersonator || isSpam || isArchived || (!isFavorite && isConversationBlockedByZillow);
  const isSelected = conversationIdFromUrl === conversationId && isDesktopView;

  const participantDisplay = useMemo(() => {
    if (isPhoneLead) {
      return conversationUtils.getPhoneLeadParticipantDisplay({
        name: renterName || '',
        phoneNumber: renterPhone || '',
      });
    }
    return conversationUtils.getParticipantsNameWithCount({
      activeParticipantsInConversation: activeParticipants,
      referenceName: renterName || '',
    });
  }, [isPhoneLead, renterName, renterPhone, activeParticipants]);

  const handleClick = useCallback(() => {
    onSelect({
      selectedConversationId: conversationId,
      selectedListingAlias: listingDetails?.listingAlias ?? '',
    });
  }, [onSelect, conversationId, listingDetails?.listingAlias]);

  return (
    <ConditionalWrapper
      shouldRenderWrapperElement={isMobileView}
      wrapper={(children) => <SwipeableWrapper conversationId={conversationId}>{children}</SwipeableWrapper>}
    >
      <ConversationItem
        conversationId={conversationId}
        isArchived={isArchived}
        isMessageOwner={isMessageOwner ?? false}
        isPhoneLead={isPhoneLead ?? false}
        isSelected={isSelected}
        isSpamOrReported={isSpam}
        isUnread={hasUnreadMessage}
        latestMessage={messagePreview ?? ''}
        latestMessageDateMs={messageDateMs != null ? String(messageDateMs) : ''}
        subtitle={streetAddress}
        title={participantDisplay}
        onClick={handleClick}
        dataEventId={CONVERSATION_ITEM_SELECT_EVENT_TYPE_ID}
        footer={
          <ConversationItemFooter
            conversationId={conversationId}
            isFavorite={isFavorite}
            statusLabel={statusLabel?.text ? { linkId: statusLabel.linkedId, text: statusLabel.text } : null}
            isStarConversationIconDisabled={isStarConversationIconDisabled}
          />
        }
      />
    </ConditionalWrapper>
  );
}
